import type { App } from '../../types';
import { useConnections, useUI } from '../../stores/appStore';
import { PlusIcon, CheckIcon } from '@heroicons/react/24/outline';
import { cn } from '../../lib/utils';

interface AppCardProps {
  app: App;
}

export function AppCard({ app }: AppCardProps) {
  const { connections } = useConnections();
  const { setSelectedApp } = useUI();
  
  const isConnected = connections.some(conn => 
    conn.appId === app.id && conn.status === 'active'
  );
  
  const connectionCount = connections.filter(conn => 
    conn.appId === app.id && conn.status === 'active'
  ).length;

  const handleConnect = () => {
    setSelectedApp(app);
  };

  return (
    <div className="bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700 p-4 sm:p-6 hover:shadow-lg transition-shadow cursor-pointer group">
      <div className="flex items-start justify-between mb-3 sm:mb-4">
        <div className="flex items-center gap-2 sm:gap-3 min-w-0">
          {(app.iconUrl || app.icon_url) ? (
            <img 
              src={app.iconUrl || app.icon_url} 
              alt={`${app.name} icon`}
              className="w-8 h-8 sm:w-10 sm:h-10 rounded-lg flex-shrink-0"
            />
          ) : (
            <div className="w-8 h-8 sm:w-10 sm:h-10 bg-gradient-to-r from-blue-500 to-purple-600 rounded-lg flex items-center justify-center flex-shrink-0">
              <span className="text-white font-bold text-xs sm:text-sm">
                {app.name.charAt(0)}
              </span>
            </div>
          )}
          <div className="flex-1 min-w-0">
            <h3 className="font-semibold text-gray-900 dark:text-white truncate text-sm sm:text-base">
              {app.name}
            </h3>
            <p className="text-xs sm:text-sm text-gray-500 dark:text-gray-400 capitalize">
              {app.category}
            </p>
          </div>
        </div>
        
        <button
          onClick={handleConnect}
          className={cn(
            "flex items-center gap-1 px-2 sm:px-3 py-1 sm:py-1.5 rounded-md text-xs sm:text-sm font-medium transition-colors flex-shrink-0 ml-2",
            isConnected
              ? "bg-green-50 text-green-700 dark:bg-green-900/20 dark:text-green-300"
              : "bg-blue-50 text-blue-700 hover:bg-blue-100 dark:bg-blue-900/20 dark:text-blue-300 dark:hover:bg-blue-900/30"
          )}
        >
          {isConnected ? (
            <>
              <CheckIcon className="w-3 h-3 sm:w-4 sm:h-4" />
              <span className="hidden xs:inline">
                {connectionCount > 1 ? `${connectionCount} Connected` : 'Connected'}
              </span>
            </>
          ) : (
            <>
              <PlusIcon className="w-3 h-3 sm:w-4 sm:h-4" />
              <span className="hidden xs:inline">Connect</span>
            </>
          )}
        </button>
      </div>

      <p className="text-xs sm:text-sm text-gray-600 dark:text-gray-300 mb-3 sm:mb-4 line-clamp-2">
        {app.description}
      </p>

      {app.tags && app.tags.length > 0 && (
        <div className="flex flex-wrap gap-1">
          {app.tags.slice(0, 3).map((tag) => (
            <span
              key={tag}
              className="inline-flex items-center px-1.5 sm:px-2 py-0.5 sm:py-1 rounded-md text-[10px] sm:text-xs font-medium bg-gray-100 text-gray-800 dark:bg-gray-700 dark:text-gray-200"
            >
              {tag}
            </span>
          ))}
          {app.tags.length > 3 && (
            <span className="inline-flex items-center px-1.5 sm:px-2 py-0.5 sm:py-1 rounded-md text-[10px] sm:text-xs font-medium bg-gray-100 text-gray-500 dark:bg-gray-700 dark:text-gray-400">
              +{app.tags.length - 3}
            </span>
          )}
        </div>
      )}
    </div>
  );
}